import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@ami/react/core';
import { MosaicWall, TextMosaic, TextMosaicDto } from '@ami/react/core';
import {
  QuoteProductConfiguration,
  QuoteCouplerStyle,
} from '../../../quotes/quoteProductConfiguration';
import {
  QuoteAttachmentPoint,
  couplerDecision,
} from '../../../quotes/otherQuoteEntities';
import { Quote } from '../../../quotes/quote';
import { CouplerOilReadyType } from '../../../coupler-styles/couplerStyle';
import QuoteWizardContext from '../../../quotes/QuoteWizardContext';
import { QuoteProductDto } from '../../../quotes/otherQuoteEntities';
import IncompatibleStockItemsModal from '../../../quotes/IncompatibleStockItemsModal';
import { ReviewQuoteCouplerDecisionModal } from './ReviewQuoteCouplerDecisionModal';
import { updateQuotePlusLeadTimesAndProducts } from '../../../quotes/quoteUpdater';
import quotePricingService from '../../quotePricingService';
import LeadTimeWarnings from '../../../quotes/LeadTimeWarnings';
import { OemThumbMountType } from '../../../machines/oemThumbMountType';
import { Alert } from 'reactstrap';
import { BoltOnMountType } from '../../../machines/machineBoltOnMountTypes';
import { BoltPatternType } from '../../../machines/machineBoltPatternTypes';
import { QuoteMachineThumbMountType } from '../../../quotes/quoteMachineThumbMountTypes';
import { Machine } from '../../../machines/machine';
import { ReviewQuoteThumbMountModal } from './ReviewQuoteThumbMountModal';
import { CouplerStyleTypes } from '../../../coupler-styles/couplerStyleTypes';
import { CouplerTypes } from '../../../coupler-styles/couplerTypes';
import { ProductGroupTypes } from '../../../products/productGroup';

const DataItem = ({ label, children }) => (
  <div className="row">
    <label className="col-sm-4 col-form-label">{label}</label>
    <div className="col-sm-8">
      <div>{children}</div>
    </div>
  </div>
);

export default class ReviewQuoteCouplerDecision extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      showModalThumbMount: false,
      loadingNewConfiguration: false,
      areAttachmentsNeeded: props.quoteAttachmentPoint.couplerDecision
        ? true
        : null,
      showIncompatibleStockItemsModal: false,
      incompatibleStockItems: [],
      boltOnMountTypes: null,
      boltPatternTypes: null,
      quoteMachineThumbMountTypes: null,
    };

    this.saved = props.onSaved;
  }
  static contextType = QuoteWizardContext;
  static propTypes = {
    quote: PropTypes.instanceOf(Quote).isRequired,
    attachmentPoint: PropTypes.object.isRequired,
    anyCouplerAffected: PropTypes.bool.isRequired,
    defaultCouplerType: PropTypes.string,
    quoteAttachmentPoint: PropTypes.instanceOf(QuoteAttachmentPoint).isRequired,
    onQuoteAttachmentPointUpdated: PropTypes.func.isRequired,
    couplerStyles: PropTypes.arrayOf(PropTypes.instanceOf(QuoteCouplerStyle))
      .isRequired,
    multiAttachmentPoints: PropTypes.bool.isRequired,
    isFromStep2: PropTypes.bool,
    isFromStep3: PropTypes.bool,
    quoteProductDto: PropTypes.instanceOf(QuoteProductDto),
    onQuoteProductDtoChange: PropTypes.func,
    showNonModalContent: PropTypes.bool,
    onloadSharedDocuments: PropTypes.func.isRequired,
    machines: PropTypes.arrayOf(PropTypes.instanceOf(Machine)).isRequired,
    onSaved: PropTypes.func,
    isFromStep3InventoryOnly: PropTypes.bool
  };
  static defaultProps = {
    isFromStep2: false,
    isFromStep3: false,
    showNonModalContent: true,
  };

  async componentDidMount() {
    const { quoteAttachmentPoint } = this.props;

    this.setState({
      boltOnMountTypes: await BoltOnMountType.all(),
      boltPatternTypes: await BoltPatternType.all(),
      quoteMachineThumbMountTypes: await QuoteMachineThumbMountType.all(),
      showModalThumbMount: this.showInitModal(quoteAttachmentPoint),
    });
  }

  hide = () => this.setState({ showModal: false });
  hideThumbMount = () => this.setState({ showModalThumbMount: false });

  save = async (decision, couplerStyleId, couplerOilReady) => {
    const updatedQuote = this.updateAttachmentPoint(
      decision,
      couplerStyleId,
      couplerOilReady
    );
    if (this.props.isFromStep2) {
      this.props.onQuoteAttachmentPointUpdated(updatedQuote);
    } else {
      await this.saveInternal(updatedQuote);
    }

    if (this.saved) {
      this.saved();
    }
  };

  saved = () => { };

  saveThumbMount = async (machineOemThumbMount, boltPattern, boltOnMount) => {
    const updatedQuote = this.updateAttachmentPointThumbMount(
      machineOemThumbMount,
      boltPattern,
      boltOnMount
    );
    if (this.props.isFromStep2) {
      this.props.onQuoteAttachmentPointUpdated(updatedQuote);
    } else {
      await this.saveInternal(updatedQuote);
    }
  };

  saveInternal = async (updatedQuote) => {
    this.setState({ loadingNewConfiguration: true });

    if (window.inventoryQuoteDataViewModel && updatedQuote.attachmentPoints?.length) {
      updatedQuote.attachmentPoints.forEach(attachmentPoint => {
        if (attachmentPoint.id === window.inventoryQuoteDataViewModel.attachmentPointId) {
          attachmentPoint.couplerDecision = window.inventoryQuoteDataViewModel.couplerDecision;
          attachmentPoint.couplerStyleId = window.inventoryQuoteDataViewModel.couplerStyleId;
        }
      });
    }

    await this.updateQuote(updatedQuote);
    this.setState({ loadingNewConfiguration: false });
    this.hide();
    this.hideThumbMount();
  };

  updateQuote = async (quote) => {
    const {
      onQuoteAttachmentPointUpdated,
      isFromStep2,
      isFromStep3,
      quoteProductDto,
      onQuoteProductDtoChange,
      onloadSharedDocuments,
      isFromStep3InventoryOnly
    } = this.props;

    if (isFromStep3 && !quoteProductDto.productId)
      return;

    const quoteProductConfiguration =
    await QuoteProductConfiguration.getProductConfigurationFromQuote(
      quote
    );

    if (isFromStep2) {
      updateQuotePlusLeadTimesAndProducts(
        quote,
        quote,
        quoteProductConfiguration,
        onQuoteAttachmentPointUpdated,
        isFromStep2,
        isFromStep3,
        quoteProductDto,
        onQuoteProductDtoChange,
        this.context.onQuoteProductConfigurationUpdate,
        (newState) => this.setState(newState),
        onloadSharedDocuments,
        isFromStep3InventoryOnly
      );
    } else {
      const copy = this.props.quote.clone();
      const payload = {
        quoteId: copy.id,
        useLatestDiscounts: copy.useLatestDiscounts,
        dealerBranchId: copy.step1.dealerBranchId,
        priceZoneId: copy.step1.priceZoneId,
        carrierId: copy.step1.carrierId,
        attachmentPoints: copy.attachmentPoints,
        products: copy.products,
        customLineItems: copy.customLineItems,
        optInDiscounts: copy.optInDiscounts
      };

      var calculateProducts = await quotePricingService.calculatePricing(payload);
      copy.updateProductsPricing(calculateProducts.products);
      copy.updateCustomLineItemsPricing(calculateProducts.customLineItems);

      updateQuotePlusLeadTimesAndProducts(
        quote,
        copy,
        quoteProductConfiguration,
        onQuoteAttachmentPointUpdated,
        isFromStep2,
        isFromStep3,
        quoteProductDto,
        onQuoteProductDtoChange,
        this.context.onQuoteProductConfigurationUpdate,
        (newState) => this.setState(newState),
        onloadSharedDocuments,
        isFromStep3InventoryOnly
      );
    }
  };

  updateAttachmentPoint = (decision, couplerStyleId, couplerOilReady) => {
    const updatedAttachmentPoint = this.props.quoteAttachmentPoint.clone();
    if (decision === couplerDecision.directPinOn) {
      updatedAttachmentPoint.setDirectPinOn();
    } else if (decision === couplerDecision.amiCoupler) {
      updatedAttachmentPoint.setAmiCoupler(couplerStyleId, couplerOilReady);
    } else if (decision === couplerDecision.existingCoupler) {
      updatedAttachmentPoint.setExistingCoupler(
        couplerStyleId,
        couplerOilReady
      );
    } else if (decision === couplerDecision.amiTiltRotator) {
      updatedAttachmentPoint.setAmiTiltRotator(couplerStyleId, couplerOilReady);
    } else if (decision === couplerDecision.existingTiltRotator) {
      updatedAttachmentPoint.setExistingTiltRotator(
        couplerStyleId,
        couplerOilReady
      );
    } else if (decision === couplerDecision.notSpecified) {
      updatedAttachmentPoint.setNotSpecified();
    } else if (decision === null) {
      updatedAttachmentPoint._initializeCouplerDecision(null);
    } else {
      throw new Error('Invalid coupler decision');
    }

    const updatedQuote = this.props.quote.clone();
    updatedQuote.updateAttachmentPoint(updatedAttachmentPoint);
    this.props.onQuoteAttachmentPointUpdated(updatedQuote);
    return updatedQuote;
  };

  updateAttachmentPointThumbMount = (
    machineOemThumbMount,
    boltPattern,
    boltOnMount
  ) => {
    const updatedAttachmentPoint = this.props.quoteAttachmentPoint.clone();
    updatedAttachmentPoint.setThumbMount(
      machineOemThumbMount,
      boltPattern,
      boltOnMount
    );
    const updatedQuote = this.props.quote.clone();
    updatedQuote.updateAttachmentPoint(updatedAttachmentPoint);
    this.props.onQuoteAttachmentPointUpdated(updatedQuote);
    return updatedQuote;
  };

  getThumbConfigurationDisplay() {
    const { quote, machines, isFromStep3, quoteAttachmentPoint } = this.props;
    const { boltOnMountTypes, boltPatternTypes, quoteMachineThumbMountTypes } =
      this.state;
    const machine = machines.find((x) => x.id === quote.step1.machineId);

    if (
      machine &&
      boltOnMountTypes &&
      boltPatternTypes &&
      quoteMachineThumbMountTypes
    ) {
      const machineAttachmentPoint = machine.attachmentPointConfigurations.find(
        (x) => x.attachmentPointId === quoteAttachmentPoint.id
      );
      const requiresThumbMount = this.getRequiresThumbMount();

      if (
        !machineAttachmentPoint?.thumbConfiguration ||
        machineAttachmentPoint.thumbConfiguration === null ||
        !requiresThumbMount
      ) {
        return '';
      }

      const hasInventoryItemAdded = quote.products?.some(x => !!x.inventoryItemId);
      const isReadOnly = isFromStep3 && quote.products.length > 0 && hasInventoryItemAdded;
      return (
        <Fragment>
          <DataItem label="OEM Thumb Mount:">
            {this.getThumbDetails(
              machineAttachmentPoint.thumbConfiguration,
              quoteAttachmentPoint
            )}
          </DataItem>
          {!isReadOnly &&
            machineAttachmentPoint.thumbConfiguration.oemThumbMount !==
            OemThumbMountType.NeverHasOemThumbMount &&
            machineAttachmentPoint.thumbConfiguration.oemThumbMount !==
            OemThumbMountType.AlwaysHasOemThumbMount && (
              <Button
                label={`${quoteAttachmentPoint.machineOemThumbMount
                  ? 'Change Thumb Mount'
                  : 'Select Thumb Mount'
                  }`}
                className="btn btn-outline-primary mt-1 offset-4"
                onClick={() => this.setState({ showModalThumbMount: true })}
              />
            )}
        </Fragment>
      );
    }
  }

  getThumbDetails(thumbConfiguration, quoteAttachmentPoint) {
    const { boltOnMountTypes, boltPatternTypes, quoteMachineThumbMountTypes } =
      this.state;

    switch (thumbConfiguration.oemThumbMount) {
      case OemThumbMountType.NeverHasOemThumbMount:
        return <div className="mt-2">No</div>;
      case OemThumbMountType.AlwaysHasOemThumbMount:
        return <div className="mt-2">Yes</div>;
      case OemThumbMountType.SometimesHasOemThumbMount:
        if (quoteAttachmentPoint.machineOemThumbMount) {
          var quoteMachineThumbMountType = quoteMachineThumbMountTypes.find(
            (x) => x.id === quoteAttachmentPoint.machineOemThumbMount
          );
          if (quoteMachineThumbMountType)
            return (
              <div className="mt-2">
                {
                  quoteMachineThumbMountTypes.find(
                    (x) => x.id === quoteAttachmentPoint.machineOemThumbMount
                  ).label
                }
              </div>
            );
        }
        return (
          <Alert color="danger" className="w-100">
            <i className="fa fa-exclamation-circle fa-lg text-danger" />{' '}
            Information Required
          </Alert>
        );
      case OemThumbMountType.SometimesHasOemThumbBoltPattern:
        if (quoteAttachmentPoint.boltPattern)
          return (
            <div>
              <div className="mt-2">
                {
                  boltPatternTypes.find(
                    (x) => x.id === quoteAttachmentPoint.boltPattern
                  ).label
                }
              </div>
              <div className="mt-2">
                {quoteAttachmentPoint.boltOnMount
                  ? boltOnMountTypes.find(
                    (x) => x.id === quoteAttachmentPoint.boltOnMount
                  ).label
                  : ''}
              </div>
            </div>
          );
        return (
          <Alert color="danger" className="w-100">
            <i className="fa fa-exclamation-circle fa-lg text-danger" />{' '}
            Information Required
          </Alert>
        );
      case OemThumbMountType.AlwaysHasOemThumbBoltPattern:
        if (
          quoteAttachmentPoint.boltPattern &&
          quoteAttachmentPoint.boltOnMount
        )
          return (
            <div>
              <div className="mt-2">
                {
                  boltPatternTypes.find(
                    (x) => x.id === quoteAttachmentPoint.boltPattern
                  ).label
                }
              </div>
              <div className="mt-2">
                {
                  boltOnMountTypes.find(
                    (x) => x.id === quoteAttachmentPoint.boltOnMount
                  ).label
                }
              </div>
            </div>
          );
        return (
          <Alert color="danger" className="w-100">
            <i className="fa fa-exclamation-circle fa-lg text-danger" />{' '}
            Information Required
          </Alert>
        );
      default:
        break;
    }
  }

  getRequiresThumbMount() {
    const { quote } = this.props;
    return quote.products.some(
      (x) =>
        x.groupType === ProductGroupTypes.StickPivotThumb.id ||
        x.groupType === ProductGroupTypes.ExtendableStickThumb.id ||
        x.groupType === ProductGroupTypes.WeldOnThumb.id
    );
  }

  showInitModal() {
    const { quote, machines, quoteAttachmentPoint } = this.props;
    const requiresThumbMount = this.getRequiresThumbMount();
    if (!requiresThumbMount) return false;

    const machine = machines.find((x) => x.id === quote.step1.machineId);
    if (machine) {
      const machineAttachmentPoint = machine.attachmentPointConfigurations.find(
        (x) => x.attachmentPointId === quoteAttachmentPoint.id
      );
      const thumbConfiguration = machineAttachmentPoint.thumbConfiguration;

      return (
        (thumbConfiguration.oemThumbMount ===
          OemThumbMountType.SometimesHasOemThumbMount &&
          quoteAttachmentPoint.machineOemThumbMount == null) ||
        (thumbConfiguration.oemThumbMount ===
          OemThumbMountType.SometimesHasOemThumbBoltPattern &&
          quoteAttachmentPoint.boltPattern == null) ||
        (thumbConfiguration.oemThumbMount ===
          OemThumbMountType.AlwaysHasOemThumbBoltPattern &&
          quoteAttachmentPoint.boltPattern == null &&
          quoteAttachmentPoint.boltOnMount == null)
      );
    }
    return false;
  }

  getThumbConfiguration() {
    const { quote, machines, attachmentPoint } = this.props;
    const machine = machines.find((x) => x.id === quote.step1.machineId);

    if (machine) {
      const attachmentPointConfiguration =
        machine.attachmentPointConfigurations.find(
          (x) => x.attachmentPointId === attachmentPoint.id
        );
      return attachmentPointConfiguration
        ? attachmentPointConfiguration.thumbConfiguration
        : null;
    }
    return null;
  }

  render() {
    const {
      showModal,
      showModalThumbMount,
      loadingNewConfiguration,
      areAttachmentsNeeded,
      showIncompatibleStockItemsModal,
      incompatibleStockItems,
      boltOnMountTypes,
      boltPatternTypes,
      quoteMachineThumbMountTypes
    } = this.state;
    const {
      quote,
      quoteAttachmentPoint,
      attachmentPoint,
      anyCouplerAffected,
      multiAttachmentPoints,
      defaultCouplerType,
      couplerStyles,
      isFromStep2,
      isFromStep3,
      showNonModalContent,
    } = this.props;
    const couplerMissing =
      !isFromStep3 &&
      quoteAttachmentPoint.couplerDecision === couplerDecision.notSpecified;
    const showRequiredForInventoryItemBanner =
      isFromStep3 &&
      (!quoteAttachmentPoint.couplerDecision ||
        quoteAttachmentPoint.couplerDecision === couplerDecision.notSpecified);

    const hasInventoryItemAdded = quote.products?.some(x => !!x.inventoryItemId);
    const isReadOnly = isFromStep3 && quote.products.length > 0 && hasInventoryItemAdded;

    const isAmiOrExistingCouplerSelection =
      quoteAttachmentPoint.couplerDecision === couplerDecision.amiCoupler ||
      quoteAttachmentPoint.couplerDecision ===
      couplerDecision.existingCoupler ||
      quoteAttachmentPoint.couplerDecision === couplerDecision.amiTiltRotator ||
      quoteAttachmentPoint.couplerDecision ===
      couplerDecision.existingTiltRotator;
    const couplerStyle = isAmiOrExistingCouplerSelection
      ? couplerStyles.find((x) => x.id === quoteAttachmentPoint.couplerStyleId)
      : null;
    const isCompatibleCouplerStyleManufacturer = isAmiOrExistingCouplerSelection
      ? couplerStyle.preferredMakes.length === 0 ||
      couplerStyle.preferredMakes.includes(quote.step1.manufacturer)
      : true;

    const informationContent = (
      <Fragment>
        {quoteAttachmentPoint.couplerDecision && (
          <DataItem label="Lugging:">
            <div className="mt-2">
              {quoteAttachmentPoint.couplerDecision ===
                couplerDecision.directPinOn && 'Direct Pin On Attachments'}
              {isAmiOrExistingCouplerSelection &&
                `${CouplerStyleTypes[couplerStyle.style]} (${CouplerTypes[couplerStyle.type]
                })`}
              {quoteAttachmentPoint.couplerDecision ===
                couplerDecision.notSpecified && 'Will be specified later'}
            </div>
          </DataItem>
        )}

        {isAmiOrExistingCouplerSelection && (
          <DataItem label="Coupler:">
            <div className="mt-2">{couplerStyle.name}</div>
          </DataItem>
        )}
        {quoteAttachmentPoint.couplerDecision &&
          quoteAttachmentPoint.couplerOilReady && (
            <DataItem label="Oil Readiness:">
              <div className="mt-2">
                {CouplerOilReadyType[quoteAttachmentPoint.couplerOilReady]}
              </div>
            </DataItem>
          )}
        {!quoteAttachmentPoint.couplerDecision && (
          <div className="mt-2 d-flex justify-content-center">Not selected</div>
        )}
        {!isCompatibleCouplerStyleManufacturer && (
          <div className="mt-2 d-flex">
            <div className="alert alert-danger" role="alert">
              <i className="fa fa-exclamation-circle" /> Lugging not compatible
              with machine, please re-select
            </div>
          </div>
        )}
        {!isReadOnly && (
          <Button
            label={
              quoteAttachmentPoint.couplerDecision
                ? 'Change Lugging'
                : 'Select Lugging'
            }
            className="btn btn-outline-primary mt-1 offset-4"
            onClick={() => this.setState({ showModal: true })}
          />
        )}
      </Fragment>
    );
    const showControls =
      !isFromStep2 || !multiAttachmentPoints || areAttachmentsNeeded;
    const containerCardValid =
      !isFromStep3 ||
      quoteAttachmentPoint.couplerDecision !== couplerDecision.notSpecified;
    const currentManufacturer = quote.step1.manufacturer;
    const thumbConfiguration = this.getThumbConfiguration();
    const requiresThumbMount = this.getRequiresThumbMount();

    return (
      <div className={multiAttachmentPoints ? 'col-12 col-md-6' : 'col'}>
        {showNonModalContent && (
          <div
            className={`card attachment-point mb-4 box-shadow quote-info ${containerCardValid ? '' : 'invalid'
              }`}
          >
            <div className="card-body p-3">
              {(multiAttachmentPoints || !isFromStep2) && !isFromStep3 && (
                <h6 className="card-title text-center">
                  {`${isFromStep2 ? '' : 'Lugging Selection '}${multiAttachmentPoints ? attachmentPoint.name : ''
                    }`}
                </h6>
              )}
              {multiAttachmentPoints && (
                <div className="text-center">
                  <img
                    src={attachmentPoint.imageUrl}
                    className="img-fluid w-50 mb-4"
                    alt={attachmentPoint.name}
                    style={{ maxWidth: '200px' }}
                  />
                </div>
              )}
              {showRequiredForInventoryItemBanner && (
                <div className="alert alert-danger text-center p-2 mb-0">
                  <strong>
                    Required in order to add a stock item to the quote
                  </strong>
                  <br />
                </div>
              )}
              {!isFromStep2 && isFromStep3 && (
                <div className="row">
                  <div className="col">{informationContent}</div>
                </div>
              )}
              {!isFromStep2 && !isFromStep3 && (
                <div className="row">
                  <div className="col">{informationContent}</div>
                </div>
              )}
              {!isFromStep2 && (
                <div className="row">
                  <div className="col">
                    {this.getThumbConfigurationDisplay()}
                  </div>
                </div>
              )}
              {isFromStep2 && multiAttachmentPoints && (
                <div
                  className={`row mb-4 justify-content-center ${areAttachmentsNeeded !== null ? '' : 'invalid'
                    }`}
                  placeholder={`Please select an answer for ${attachmentPoint.name}`}
                >
                  <div className="col">
                    <div className="row justify-content-center text-center">
                      <h5>
                        Will you need attachments for the {attachmentPoint.name}
                        ?
                      </h5>
                    </div>
                    <MosaicWall childrenContainerClassName="row justify-content-center">
                      <TextMosaic
                        data={
                          new TextMosaicDto({
                            label: 'No',
                            selected: areAttachmentsNeeded === false,
                          })
                        }
                        onClick={() => {
                          this.setState({ areAttachmentsNeeded: false });
                          this.updateAttachmentPoint(null);
                        }}
                        widthColumns={4}
                        mdWidthColumns={4}
                      />
                      <TextMosaic
                        data={
                          new TextMosaicDto({
                            label: 'Yes',
                            selected: areAttachmentsNeeded === true,
                          })
                        }
                        onClick={() =>
                          this.setState({ areAttachmentsNeeded: true })
                        }
                        widthColumns={4}
                        mdWidthColumns={4}
                      />
                    </MosaicWall>
                  </div>
                </div>
              )}
              {showControls && !isReadOnly && (
                <ReviewQuoteCouplerDecisionModal
                  show={showModal}
                  onSave={this.save}
                  onHide={this.hide}
                  attachmentPoint={attachmentPoint}
                  anyCouplerAffected={anyCouplerAffected}
                  defaultCouplerType={defaultCouplerType}
                  couplerStyles={couplerStyles}
                  loadingNewConfiguration={loadingNewConfiguration}
                  currentDecision={quoteAttachmentPoint.couplerDecision}
                  currentCouplerStyleId={quoteAttachmentPoint.couplerStyleId}
                  currentCouplerOilReady={quoteAttachmentPoint.couplerOilReady}
                  productGroupIdsPerType={
                    quoteAttachmentPoint.productGroupIdsPerType
                  }
                  isFromStep2={isFromStep2}
                  isNarrowLayout={showModal || multiAttachmentPoints}
                  currentManufacturer={currentManufacturer}
                />
              )}
              {showControls && !isReadOnly && (
                <ReviewQuoteThumbMountModal
                  quoteAttachmentPoint={quoteAttachmentPoint}
                  attachmentPoint={attachmentPoint}
                  show={showModalThumbMount}
                  onSave={this.saveThumbMount}
                  onHide={this.hideThumbMount}
                  loadingNewConfiguration={loadingNewConfiguration}
                  thumbConfiguration={thumbConfiguration}
                  boltOnMountTypes={boltOnMountTypes}
                  boltPatternTypes={boltPatternTypes}
                  quoteMachineThumbMountTypes={quoteMachineThumbMountTypes}
                  requiresThumbMount={requiresThumbMount}
                />
              )}
              {isFromStep2 && (
                <LeadTimeWarnings
                  machineMissing={false}
                  couplerMissing={couplerMissing}
                />
              )}
            </div>
          </div>
        )}
        {showIncompatibleStockItemsModal && (
          <IncompatibleStockItemsModal
            incompatibleStockItems={incompatibleStockItems}
            onCloseClicked={() =>
              this.setState({ showIncompatibleStockItemsModal: false })
            }
          />
        )}
      </div>
    );
  }
}
